import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-600", review: "bg-amber-100 text-amber-700",
  approved: "bg-blue-100 text-blue-700", scheduled: "bg-purple-100 text-purple-700",
  published: "bg-emerald-100 text-emerald-700", archived: "bg-slate-200 text-slate-500",
};

export default function AdminContentList() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [meta, setMeta] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (typeFilter) params.set("type", typeFilter);
    api.get(`/api/admin/content?${params.toString()}`).then(({ data }) => setData(data)).catch(() => {});
  }, [q, typeFilter]);

  useEffect(() => { api.get("/api/admin/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  async function del(id) {
    if (!window.confirm("Delete this content permanently?")) return;
    try { await api.delete(`/api/admin/content/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed (admin only)"); }
  }

  async function changeStatus(id, status) {
    await api.patch(`/api/admin/content/${id}/status?status=${status}`);
    toast.success(`Marked ${status}`); load();
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Content</h1>
          <p className="mt-1 text-sm text-slate-500">{data?.total ?? 0} items</p>
        </div>
        <Button asChild className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="admin-new-content">
          <Link to="/admin/content/new"><Plus className="mr-1.5 h-4 w-4" />New content</Link>
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Input placeholder="Search titles…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-content-search" className="max-w-xs" />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="admin-type-filter" className="rounded-md border border-slate-200 px-3 text-sm">
          <option value="">All types</option>
          {meta?.content_types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Locales</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data?.items.map((it) => (
              <tr key={it.id} data-testid={`admin-row-${it.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-800">{it.title}{it.is_demo && <span className="ml-2 rounded bg-amber-100 px-1.5 text-[10px] font-bold text-amber-700">DEMO</span>}{it.is_featured && <span className="ml-2 rounded bg-emerald-100 px-1.5 text-[10px] font-bold text-emerald-700">FEATURED</span>}</td>
                <td className="px-4 py-3 capitalize text-slate-500">{it.type}</td>
                <td className="px-4 py-3 text-xs uppercase text-slate-400">{(it.available_locales || []).join(" ")}</td>
                <td className="px-4 py-3">
                  <select value={it.status} onChange={(e) => changeStatus(it.id, e.target.value)} data-testid={`status-${it.id}`}
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${STATUS_COLORS[it.status] || ""}`}>
                    {meta?.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button asChild variant="ghost" size="icon" data-testid={`edit-${it.id}`}><Link to={`/admin/content/${it.id}/edit`}><Pencil className="h-4 w-4" /></Link></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(it.id)} data-testid={`delete-${it.id}`} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No content found.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
